import { useCallback, useEffect, useState } from 'react';
import type { WidgetConfig, ProductImage } from '../lib/config';
import { colorize, fetchQuota, type QuotaSnapshot } from '../lib/api';
import { t } from '../lib/strings';
import { BeforeAfter } from './BeforeAfter';
import { Disclaimer } from './Disclaimer';

const MAX_MB = 6;

/** Download filename with the right extension, derived from the result data-URL mime. */
function downloadName(dataUrl: string): string {
  const ext = dataUrl.startsWith('data:image/jpeg')
    ? 'jpg'
    : dataUrl.startsWith('data:image/webp')
      ? 'webp'
      : 'png';
  return `spezzial-ki-folierung.${ext}`;
}

export function Modal({ config, onClose }: { config: WidgetConfig; onClose: () => void }) {
  const [product, setProduct] = useState<ProductImage | null>(config.productImages[0] ?? null);
  const [vehicleUrl, setVehicleUrl] = useState<string | null>(null);
  const [vehicleMime, setVehicleMime] = useState<string>('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [quota, setQuota] = useState<QuotaSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    let active = true;
    fetchQuota(config).then((q) => {
      if (active && q) setQuota(q);
    });
    return () => {
      active = false;
    };
  }, [config]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError(t('err_type'));
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(t('err_size', { mb: MAX_MB }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setVehicleUrl(String(reader.result));
      setVehicleMime(file.type);
      setFileName(file.name);
      setResult(null);
    };
    reader.readAsDataURL(file);
  }, []);

  const apply = useCallback(async () => {
    if (!product) {
      setError(t('err_no_product'));
      return;
    }
    if (!vehicleUrl) {
      setError(t('err_no_vehicle'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await colorize(config, {
        vehicleImage: vehicleUrl,
        vehicleMime,
        productId: config.productId,
        productImageId: product.id,
        productImageUrl: product.url,
        productName: config.productName,
      });
      if (data.status === 429) {
        if (data.quota) setQuota(data.quota);
        setError(t('err_quota'));
        return;
      }
      if (data.status < 200 || data.status >= 300 || !data.image) {
        setError(data.message ?? t('err_generic'));
        return;
      }
      setResult(data.image);
      if (data.quota) setQuota(data.quota);
    } catch {
      setError(t('err_generic'));
    } finally {
      setBusy(false);
    }
  }, [product, vehicleUrl, vehicleMime, config]);

  return (
    <div className="aiw-overlay" onClick={onClose} role="presentation">
      <div
        className="aiw-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t('modal_title')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aiw-dialog-head">
          <h2>{t('modal_title')}</h2>
          <button type="button" className="aiw-close" aria-label={t('close')} onClick={onClose}>
            ×
          </button>
        </div>

        {config.productImages.length === 0 ? (
          <p className="aiw-muted">{t('no_images')}</p>
        ) : result ? (
          /* Result view — replaces the form once a preview is generated. */
          <div className="aiw-body">
            <section className="aiw-field">
              <span className="aiw-label">{t('result_label')}</span>
              {vehicleUrl ? (
                <BeforeAfter
                  before={vehicleUrl}
                  after={result}
                  variant="result"
                  beforeAlt={t('upload_alt')}
                  afterAlt={t('result_alt')}
                />
              ) : (
                <img className="aiw-result" src={result} alt={t('result_alt')} />
              )}
              <Disclaimer url={config.privacyUrl} label={config.privacyLabel} variant="legal" />
            </section>
            <div className="aiw-actions">
              <div className="aiw-actions-buttons">
                <a className="aiw-btn" href={result} download={downloadName(result)}>
                  {t('download')}
                </a>
                <button type="button" className="aiw-btn aiw-btn-ghost" onClick={() => setResult(null)}>
                  {t('new_preview')}
                </button>
              </div>
              {quota && (
                <span className="aiw-quota aiw-muted aiw-small">
                  {t('remaining', { remaining: quota.remaining, limit: quota.limit })}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="aiw-body">
            {/* Step 1 — pick the wrap (product image) */}
            <section className="aiw-step">
              <div className="aiw-step-head">
                <span className="aiw-step-num">1</span>
                <span className="aiw-label">{t('step_product')}</span>
              </div>
              <p className="aiw-muted aiw-small">{t('step_product_hint')}</p>
              <div className="aiw-thumbs">
                {config.productImages.map((img) => (
                  <button
                    key={img.id}
                    type="button"
                    className={`aiw-thumb${product?.id === img.id ? ' is-selected' : ''}`}
                    aria-pressed={product?.id === img.id}
                    title={img.alt}
                    onClick={() => setProduct(img)}
                  >
                    <img src={img.url} alt={img.alt} />
                  </button>
                ))}
              </div>
            </section>

            {/* Step 2 — upload the vehicle photo */}
            <section className="aiw-step">
              <div className="aiw-step-head">
                <span className="aiw-step-num">2</span>
                <span className="aiw-label">{t('step_vehicle')}</span>
              </div>
              <p className="aiw-muted aiw-small">{t('step_vehicle_hint')}</p>
              <label className="aiw-upload">
                <input
                  type="file"
                  accept="image/*"
                  onChange={onFile}
                  className="aiw-visually-hidden"
                />
                <span className="aiw-upload-btn">{fileName ? t('change_file') : t('choose_file')}</span>
                <span className="aiw-upload-name">{fileName ?? t('no_file')}</span>
              </label>
              <label className="aiw-consent">
                <input
                  type="checkbox"
                  className="aiw-consent-box"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <Disclaimer
                  url={config.privacyUrl}
                  label={config.privacyLabel}
                  variant="privacy"
                  inline
                />
              </label>
              {vehicleUrl && <img className="aiw-preview" src={vehicleUrl} alt={t('upload_alt')} />}
            </section>

            {/* Action */}
            <div className="aiw-actions">
              <div className="aiw-actions-buttons">
                <button
                  type="button"
                  className="aiw-btn"
                  onClick={apply}
                  disabled={busy || !product || !vehicleUrl || !consent}
                >
                  {busy ? t('applying') : t('apply')}
                </button>
              </div>
              {quota && (
                <span className="aiw-quota aiw-muted aiw-small">
                  {t('remaining', { remaining: quota.remaining, limit: quota.limit })}
                </span>
              )}
            </div>

            {error && <p className="aiw-error">{error}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
